import { useLayoutEffect, useRef, useState } from "react";
import { X, FilePdf } from "@phosphor-icons/react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";

const PAGE_W = 1123; // A4 landscape width @ ~96dpi

export function PrintPreviewModal({ open, onClose, title, subtitle, logoSrc, fileName, children }) {
  const pageRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(0.3);
  const [wrapH, setWrapH] = useState(0);
  const [busy, setBusy] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    const calc = () => {
      if (!containerRef.current || !pageRef.current) return;
      const avail = containerRef.current.clientWidth - 24;
      const s = Math.min(1, avail / PAGE_W);
      setScale(s);
      setWrapH(pageRef.current.offsetHeight * s);
    };
    const id = requestAnimationFrame(calc);
    window.addEventListener("resize", calc);
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", calc); };
  }, [open, children]);

  if (!open) return null;

  const exportPdf = async () => {
    setBusy(true);
    try {
      const canvas = await html2canvas(pageRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const landscape = canvas.width >= canvas.height;
      const pdf = new jsPDF({ orientation: landscape ? "l" : "p", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 18;
      const ratio = Math.min((pw - m * 2) / canvas.width, (ph - m * 2) / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(img, "PNG", (pw - w) / 2, (ph - h) / 2, w, h);
      pdf.save(fileName || "preview.pdf");
      toast.success("PDF diunduh");
    } catch (e) {
      toast.error("Gagal membuat PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 flex flex-col" data-testid="print-preview-modal">
      <div className="flex items-center justify-between px-4 py-3 bg-stone-900 text-white shrink-0">
        <button data-testid="preview-close" onClick={onClose} className="flex items-center gap-1.5 text-sm font-medium active:scale-95 transition-transform"><X size={18} /> Tutup</button>
        <span className="text-xs text-stone-300 truncate px-2">{title}</span>
        <button data-testid="preview-export-pdf" disabled={busy} onClick={exportPdf} className="bg-red-600 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-60">
          <FilePdf size={18} weight="bold" /> {busy ? "Membuat…" : "Export PDF"}
        </button>
      </div>
      <div ref={containerRef} className="flex-1 overflow-auto p-3 flex justify-center">
        <div style={{ width: PAGE_W * scale, height: wrapH || undefined }}>
          <div style={{ width: PAGE_W, transform: `scale(${scale})`, transformOrigin: "top left" }}>
            <div ref={pageRef} style={{ width: PAGE_W, minHeight: 794, background: "#ffffff", padding: 36, position: "relative", boxSizing: "border-box" }}>
              {logoSrc && (
                <img src={logoSrc} alt="" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 540, maxWidth: "70%", opacity: 0.25, pointerEvents: "none" }} />
              )}
              <div style={{ position: "relative" }}>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <h2 style={{ fontSize: 24, fontWeight: 800, color: "#1c1917", margin: 0 }}>{title}</h2>
                  {subtitle && <p style={{ fontSize: 15, color: "#78716c", margin: "4px 0 0" }}>{subtitle}</p>}
                </div>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
